import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { API_URL, expireSession, isSessionEndpoint, refreshAccessToken } from './auth-session';
import { getErrorMessage } from './error-message';

type RetryableRequestConfig = InternalAxiosRequestConfig & { _sessionRetry?: boolean };

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - add JWT token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    error.message = getErrorMessage(error);
    const config = error.config as RetryableRequestConfig | undefined;
    if (error.response?.status === 401 && config && !isSessionEndpoint(config.url ?? '')) {
      const authorization = config.headers.get('Authorization');
      const failedToken =
        typeof authorization === 'string' ? authorization.replace(/^Bearer /, '') : null;
      if (config._sessionRetry || !failedToken) {
        expireSession(failedToken);
      } else {
        config._sessionRetry = true;
        const token = await refreshAccessToken(failedToken);
        config.headers.set('Authorization', `Bearer ${token}`);
        return api.request(config);
      }
    }
    return Promise.reject(error);
  },
);

export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
  details?: unknown;
}

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error) && error.response?.data?.error !== undefined;
}

// Prefer the backend message over Axios' generic "Request failed with status code X"
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error) && error.response?.data?.message) {
    return error.response.data.message;
  }
  return fallback;
}

// Helper function for fetch with authentication
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('token');
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401 && !isSessionEndpoint(url)) {
    if (!token) {
      expireSession(token);
      return response;
    }
    const refreshedToken = await refreshAccessToken(token);
    headers.set('Authorization', `Bearer ${refreshedToken}`);
    const retry = await fetch(url, { ...options, headers });
    if (retry.status === 401) expireSession(refreshedToken);
    return retry;
  }

  return response;
}

// Helper function to get user from JWT token
export function getUserFromToken(): { id: string; name: string } | null {
  const token = localStorage.getItem('token');
  if (!token) return null;

  try {
    // JWT token format: header.payload.signature
    const payload = token.split('.')[1];
    if (!payload) return null;

    // Decode base64url to base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed
    const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    // Parse JSON
    const decoded = JSON.parse(atob(paddedBase64));

    // BWAUTH access tokens carry both a numeric internal `id` (e.g. 47) and the
    // real bcube user UUID under `userId` / `app.id`. We must use the UUID — the
    // numeric `id` is BWAUTH-internal and is not a valid bcube user id (it also
    // breaks `author.user_uuid` and string-typed user.id fields downstream). The
    // user's name/email live under `app` in BWAUTH tokens; fall back to the
    // legacy local-token claims for non-BWAUTH tokens.
    const app = decoded.app ?? {};
    const rawId = decoded.userId ?? app.id ?? decoded.sub ?? decoded.id ?? '';

    return {
      id: String(rawId),
      name: app.name || decoded.name || decoded.username || app.email || decoded.email || 'Unknown',
    };
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
}
